import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useStore } from '../context/StoreContext';
import {
  Users,
  Shield,
  FileText,
  Search,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Printer,
  AlertTriangle,
  Settings,
  Check,
  Truck,
  ShoppingCart,
  Receipt,
  Download,
  FileSpreadsheet
} from 'lucide-react';

// Warranty categories with default terms
const WARRANTY_CATEGORIES = [
  { value: 'smartphone', label: 'Smartphone', defaultMonths: 12 },
  { value: 'laptop', label: 'Laptop', defaultMonths: 24 },
  { value: 'tablet', label: 'Tablet', defaultMonths: 12 },
  { value: 'tv', label: 'Televizor', defaultMonths: 24 },
  { value: 'ac', label: 'Kondicionier', defaultMonths: 36 },
  { value: 'appliance', label: 'Elektroshtëpiake', defaultMonths: 24 },
  { value: 'accessory', label: 'Aksesor', defaultMonths: 6 },
  { value: 'repair', label: 'Riparim', defaultMonths: 3 },
  { value: 'other', label: 'Tjetër', defaultMonths: 12 },
];

// Default warranty terms by category
const DEFAULT_WARRANTY_TERMS = {
  ac: {
    covered: [
      'Klienti i prezanton shitesit defektin e paisjes brenda afatit kohor te percaktuar ne faturen e shitjes.',
      'Defekti eshte rezultat i problemeve te prodhimit dhe jo keqperdorimit.',
    ],
    actions: [
      'Riparuar (falas) ose duke e kthyer ne gjendje pune paisjen difektoze me probleme fabrikimi.',
      'Duke zëvendësuar me pjesë te reja origjinale të paisjes.',
    ],
    excluded: [
      'Vetë-riparimi ose riparimi nga persona/shërbime jo të autorizuara.',
      'Dëmtimi i aparatit nga luhatjet e tensionit jashtë limiteve të përcaktuara.',
      'Dëmtimet mekanike të shkaktuara nga (goditje, transport etj).',
      'Dëmtimi i shkaktuar nga përdorimi i pakujdesshëm ose force majeure.',
    ]
  },
  smartphone: {
    covered: [
      'Klienti i prezanton shitesit fletën e garancisë brenda afatit kohor të përcaktuar.',
      'Defekti eshte rezultat i problemeve te prodhimit dhe jo keqperdorimit.',
    ],
    actions: [
      'Riparuar (falas) ose duke e kthyer në gjendje pune paisjen difektoze.',
      'Duke zëvendësuar me pjesë të reja origjinale.',
    ],
    excluded: [
      'Bazën ngarkuese, Baterinë, Ngarkuesin, Mbulesë të tjera',
      'Dëmtime fizike (thyerje xhami, goditje, lagështi)',
      'Probleme nga software jo origjinal',
      'Vetë-riparimi ose riparimi nga persona jo të autorizuar',
    ]
  },
  default: {
    covered: [
      'Produkti prezanton defekt fabrikimi brenda afatit të garancisë.',
      'Fatura origjinale dhe karta e garancisë prezantohen.',
    ],
    actions: [
      'Riparuar (falas) ose duke e zëvendësuar produktin.',
    ],
    excluded: [
      'Dëmtime fizike, nga uji ose përdorim jo të duhur.',
      'Vetë-riparimi ose riparimi nga persona jo të autorizuar.',
      'Pjesët konsumuese (kabllo, adaptera etj).',
    ]
  }
};

export default function Klientet() {
  const [activeTab, setActiveTab] = useState('customers');
  const [loading, setLoading] = useState(true);

  // Store context
  const { currentStore, stores, canSwitchStores, refreshStores } = useStore();

  // Helper to get store name by id
  const getStoreName = (storeId) => {
    const store = stores?.find(s => s.id === storeId);
    return store?.name || '-';
  };

  // Show store column when viewing all stores
  const showStoreColumn = canSwitchStores && !currentStore;

  // Hide warranty-related tabs for boutique stores
  const showWarrantyTabs = !currentStore || !['boutique', 'luxstore', 'botique', 'luxshop'].includes(currentStore.type?.toLowerCase());

  // Shared data
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [products, setProducts] = useState([]);
  const [warrantyTemplates, setWarrantyTemplates] = useState([]);
  const [debts, setDebts] = useState([]);
  const [sales, setSales] = useState([]);

  // Search and filters
  const [customerSearch, setCustomerSearch] = useState('');
  const [warrantySearch, setWarrantySearch] = useState('');
  const [debtSearch, setDebtSearch] = useState('');
  const [salesSearch, setSalesSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [debtFilter, setDebtFilter] = useState('all');
  const [salesCustomerFilter, setSalesCustomerFilter] = useState('all');

  // Modals
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isWarrantyModalOpen, setIsWarrantyModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDebtPaymentModalOpen, setIsDebtPaymentModalOpen] = useState(false);
  const [isCustomerWarrantiesOpen, setIsCustomerWarrantiesOpen] = useState(false);
  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isEditSaleOpen, setIsEditSaleOpen] = useState(false);
  const [isManualDebtModalOpen, setIsManualDebtModalOpen] = useState(false);
  const [isEditDebtModalOpen, setIsEditDebtModalOpen] = useState(false);
  const [editDebtAmount, setEditDebtAmount] = useState(0);
  const [manualDebtForm, setManualDebtForm] = useState({ customer_id: '', amount: 0, date: new Date().toISOString().split('T')[0], notes: '' });

  // Selected items
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [customerWarranties, setCustomerWarranties] = useState([]);
  const [editingSale, setEditingSale] = useState(null);
  const [editPaid, setEditPaid] = useState(0);
  const [editPaymentMethod, setEditPaymentMethod] = useState('cash');

  // Forms
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', email: '', address: '', debt: 0 });
  const [warrantyForm, setWarrantyForm] = useState({
    type: 'product', category: 'smartphone', product_id: '', product_name: '', serial_number: '',
    customer_id: '', customer_name: '', start_date: new Date().toISOString().split('T')[0],
    end_date: '', duration_months: 12, notes: '', status: 'active',
  });
  const [templateForm, setTemplateForm] = useState({
    category: 'smartphone', name: '', duration_months: 12,
    covered_terms: '', action_terms: '', excluded_terms: '',
    logo_url: '', store_name: 'MIRI SHOP'
  });
  const [newCustomerData, setNewCustomerData] = useState({ name: '', phone: '', address: '' });
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [debtPaymentAmount, setDebtPaymentAmount] = useState(0);

  useEffect(() => { loadAllData(); }, [currentStore]);

  const loadAllData = async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [customersRes, suppliersRes, warrantiesRes, productsRes, salesRes] = await Promise.all([
        api.get(`/customers${storeParam}`),
        api.get(`/suppliers${storeParam}`),
        api.get(`/warranties${storeParam}`),
        api.get(`/products${storeParam}`),
        api.get(`/sales${storeParam}`)
      ]);
      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setWarranties(warrantiesRes.data);
      setProducts(productsRes.data);
      setSales(salesRes.data);

      // Build debts list from customers and suppliers
      const customerDebts = customersRes.data
        .filter(c => c.debt > 0)
        .map(c => ({ ...c, type: 'customer', entity_name: c.name }));
      const supplierDebts = suppliersRes.data
        .filter(s => s.debt > 0)
        .map(s => ({ ...s, type: 'supplier', entity_name: s.name }));
      setDebts([...customerDebts, ...supplierDebts].sort((a, b) => b.debt - a.debt));

      // Load warranty templates directly from currentStore (backend synced)
      if (currentStore?.warranty_templates) {
        setWarrantyTemplates(currentStore.warranty_templates);
      } else {
        setWarrantyTemplates([]);
      }
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.phone?.includes(customerSearch)
  );
  const filteredWarranties = warranties.filter(w => {
    const matchesSearch = w.product_name.toLowerCase().includes(warrantySearch.toLowerCase()) ||
      w.customer_name.toLowerCase().includes(warrantySearch.toLowerCase());
    const matchesType = typeFilter === 'all' || w.type === typeFilter;
    return matchesSearch && matchesType;
  });
  const filteredDebts = debts.filter(d => {
    const matchesSearch = d.entity_name.toLowerCase().includes(debtSearch.toLowerCase());
    const matchesFilter = debtFilter === 'all' ||
      (debtFilter === 'customers' && d.type === 'customer') ||
      (debtFilter === 'suppliers' && d.type === 'supplier');
    return matchesSearch && matchesFilter;
  });
  const filteredSales = sales.filter(s => {
    const matchesSearch = s.customer_name?.toLowerCase().includes(salesSearch.toLowerCase()) ||
      s.id?.toLowerCase().includes(salesSearch.toLowerCase());
    const matchesCustomer = salesCustomerFilter === 'all' || s.customer_id === salesCustomerFilter;
    return matchesSearch && matchesCustomer;
  });

  // Stats
  const totalCustomerDebt = customers.reduce((sum, c) => sum + (c.debt || 0), 0);
  const totalSupplierDebt = suppliers.reduce((sum, s) => sum + (s.debt || 0), 0);
  const expiringWarranties = warranties.filter(w => {
    const end = new Date(w.end_date);
    const now = new Date();
    const diff = (end - now) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 30;
  });
  const totalSalesRevenue = sales.reduce((sum, s) => sum + (s.total || 0), 0);
  const totalSalesDebt = sales.reduce((sum, s) => sum + (s.debt || 0), 0);

  // Helper functions
  const isExpired = (endDate) => new Date(endDate) < new Date();
  const isExpiringSoon = (endDate) => {
    const diff = (new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 30;
  };
  const calculateEndDate = (startDate, months) => {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + months);
    return date.toISOString();
  };
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('sq-AL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Sales handlers
  const handleDeleteSale = async (sale) => {
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë shitje? Stoku do të rikthehet.')) return;
    try {
      await api.delete(`/sales/${sale.id}`);
      toast.success('Shitja u fshi dhe stoku u rikthye');
      loadAllData();
    } catch (e) {
      toast.error('Gabim në fshirje');
    }
  };

  const handleUpdateSale = async () => {
    try {
      const newDebt = Math.max(0, editingSale.total - editPaid);
      await api.put(`/sales/${editingSale.id}`, {
        ...editingSale,
        paid: editPaid,
        debt: newDebt,
        payment_method: editPaymentMethod
      });
      toast.success('Shitja u përditësua');
      setIsEditSaleOpen(false);
      loadAllData();
    } catch (e) {
      toast.error('Gabim në përditësim');
    }
  };

  const printSaleReceipt = (sale) => {
    // Get invoice settings from current store or use defaults
    const invoiceSettings = {
      font_family: currentStore?.invoice_font_family || 'Arial',
      width: currentStore?.receipt_width === '58mm' ? '58mm' : currentStore?.receipt_width === 'A4' ? '210mm' : '80mm',
      primary_color: currentStore?.invoice_primary_color || '#000000',
      show_logo: currentStore?.invoice_show_logo !== false,
      show_nipt: currentStore?.invoice_show_nipt !== false,
      show_address: currentStore?.invoice_show_address !== false,
      show_phone: currentStore?.invoice_show_phone !== false,
      header_text: currentStore?.invoice_header || '',
      footer_text: currentStore?.invoice_footer || 'Faleminderit!',
      logo_url: currentStore?.logo_url || '',
      store_name: currentStore?.name || 'MIRI SHOP',
      address: currentStore?.address || '',
      phone: currentStore?.phone || '',
      nipt: currentStore?.nipt || ''
    };

    const receiptWindow = window.open('', '_blank', 'width=400,height=700');
    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Faturë - ${sale?.id?.slice(0, 8)}</title>
          <style>
            @page { margin: 0; size: ${invoiceSettings.width} auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: '${invoiceSettings.font_family}', sans-serif; 
              padding: 10px; 
              width: ${invoiceSettings.width}; 
              font-size: 12px;
            }
            .header { 
              text-align: center; 
              margin-bottom: 15px; 
              border-bottom: 1px dashed ${invoiceSettings.primary_color}; 
              padding-bottom: 10px; 
            }
            .header h2 { 
              font-size: 18px; 
              margin-bottom: 5px; 
              color: ${invoiceSettings.primary_color};
              text-transform: uppercase;
            }
            .store-logo {
              max-height: 60px;
              max-width: 100%;
              margin-bottom: 5px;
            }
            .info { margin-bottom: 10px; font-size: 11px; }
            .info p { margin: 2px 0; }
            .line { border-top: 1px dashed #000; margin: 10px 0; }
            .items { margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 11px; }
            .totals { margin-top: 10px; }
            .totals .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .totals .total { 
              font-size: 16px; 
              font-weight: bold; 
              margin-top: 5px; 
              color: ${invoiceSettings.primary_color};
            }
            .reprint { text-align: center; margin-top: 10px; font-size: 10px; color: #666; border-top: 1px dashed #000; padding-top: 5px; }
            .footer { text-align: center; margin-top: 15px; font-size: 11px; font-style: italic; }
            .header-msg { font-style: italic; font-weight: bold; margin-bottom: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            ${invoiceSettings.show_logo && invoiceSettings.logo_url ? `<img src="${invoiceSettings.logo_url}" class="store-logo" />` : ''}
            <h2>${invoiceSettings.store_name}</h2>
            
            <div style="font-size: 10px; color: #555;">
              ${invoiceSettings.show_address && invoiceSettings.address ? `<p>${invoiceSettings.address}</p>` : ''}
              ${invoiceSettings.show_phone && invoiceSettings.phone ? `<p>Tel: ${invoiceSettings.phone}</p>` : ''}
              ${invoiceSettings.show_nipt && invoiceSettings.nipt ? `<p>NIPT: ${invoiceSettings.nipt}</p>` : ''}
            </div>

            ${invoiceSettings.header_text ? `<p class="header-msg" style="margin-top: 8px;">${invoiceSettings.header_text}</p>` : ''}
          </div>

          <div class="info">
            <p><strong>Nr:</strong> ${sale?.id?.slice(0, 8).toUpperCase()}</p>
            <p><strong>Data:</strong> ${formatDateTime(sale?.created_at)}</p>
            <p><strong>Klienti:</strong> ${sale?.customer_name}</p>
          </div>

          <div class="line"></div>

          <div class="items">
            ${sale?.items?.map(item => `
              <div class="item">
                <span>${item.product_name}</span>
                <span>x${item.quantity} = ${formatCurrency(item.total)}</span>
              </div>
            `).join('')}
          </div>

          <div class="line"></div>

          <div class="totals">
            <div class="row"><span>Nëntotal:</span><span>${formatCurrency(sale?.subtotal)}</span></div>
            ${sale?.discount > 0 ? `<div class="row"><span>Zbritje:</span><span>-${formatCurrency(sale?.discount)}</span></div>` : ''}
            <div class="row total"><span>TOTAL:</span><span>${formatCurrency(sale?.total)}</span></div>
            <div class="row"><span>Paguar:</span><span>${formatCurrency(sale?.paid)}</span></div>
            ${sale?.debt > 0 ? `<div class="row" style="color:#c00;"><span>Borxh:</span><span>${formatCurrency(sale?.debt)}</span></div>` : ''}
          </div>

          <div class="reprint">** RI-PRINTIM **</div>

          <div class="footer">
            <div class="line"></div>
            <p>${invoiceSettings.footer_text}</p>
          </div>
          
          <script>
            window.onload = function() { 
              // Wait for image to load if present
              if (document.images.length > 0) {
                var img = document.images[0];
                if (img.complete) window.print();
                else img.onload = function() { window.print(); };
              } else {
                window.print();
              }
            }
          </script>
        </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  // Export to Excel
  const exportToExcel = () => {
    const data = filteredSales.map(sale => ({
      'Data': formatDateTime(sale.created_at),
      'ID': sale.id?.slice(0, 8),
      'Klienti': sale.customer_name,
      'Artikuj': sale.items?.map(i => `${i.product_name} x${i.quantity}`).join('; '),
      'Total': sale.total,
      'Paguar': sale.paid,
      'Borxh': sale.debt
    }));

    // Create CSV content
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(','));
    const csv = [headers, ...rows].join('\n');

    // Download file
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shitjet_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Skedari Excel u shkarkua');
  };

  // Export to PDF
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lista e Shitjeve</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            .header p { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 10px; }
            th { background: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background: #fafafa; }
            .text-right { text-align: right; }
            .total-row { font-weight: bold; background: #e5e5e5 !important; }
            .debt { color: #c00; }
            .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>MIRI SHOP - Lista e Shitjeve</h1>
            <p>Eksportuar: ${new Date().toLocaleString('sq-AL')}</p>
            <p>Total: ${filteredSales.length} shitje</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Klienti</th>
                <th>Artikuj</th>
                <th class="text-right">Total</th>
                <th class="text-right">Paguar</th>
                <th class="text-right">Borxh</th>
              </tr>
            </thead>
            <tbody>
              ${filteredSales.map(sale => `
                <tr>
                  <td>${formatDateTime(sale.created_at)}</td>
                  <td>${sale.customer_name}</td>
                  <td style="font-size:9px;">${sale.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}</td>
                  <td class="text-right">${formatCurrency(sale.total)}</td>
                  <td class="text-right">${formatCurrency(sale.paid)}</td>
                  <td class="text-right ${sale.debt > 0 ? 'debt' : ''}">${sale.debt > 0 ? formatCurrency(sale.debt) : '-'}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3">TOTAL</td>
                <td class="text-right">${formatCurrency(totalSalesRevenue)}</td>
                <td class="text-right">${formatCurrency(filteredSales.reduce((sum, s) => sum + s.paid, 0))}</td>
                <td class="text-right debt">${formatCurrency(totalSalesDebt)}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="footer">
            <p>Raport i gjeneruar automatikisht</p>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Customer handlers
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...customerForm };
      if (currentStore?.id) {
        submitData.store_id = currentStore.id;
      }
      if (selectedCustomer) {
        await api.put(`/customers/${selectedCustomer.id}`, submitData);
        toast.success('Klienti u përditësua');
      } else {
        await api.post('/customers', submitData);
        toast.success('Klienti u shtua');
      }
      setIsCustomerModalOpen(false);
      resetCustomerForm();
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleCustomerDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success('Klienti u fshi');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const handlePayment = async () => {
    if (paymentAmount <= 0) {
      toast.error('Shuma duhet të jetë më e madhe se 0');
      return;
    }
    try {
      const paymentData = {
        entity_type: 'customer', entity_id: selectedCustomer.id,
        entity_name: selectedCustomer.name, amount: paymentAmount, notes: 'Pagesë borxhi'
      };
      if (currentStore?.id) {
        paymentData.store_id = currentStore.id;
      }
      await api.post('/debt-payments', paymentData);
      toast.success('Pagesa u regjistrua');
      setIsPaymentModalOpen(false);
      setPaymentAmount(0);
      loadAllData();
    } catch (error) {
      toast.error('Gabim në regjistrimin e pagesës');
    }
  };

  // Debt payment handler (for debts tab)
  const handleDebtPayment = async () => {
    if (debtPaymentAmount <= 0) {
      toast.error('Shuma duhet të jetë më e madhe se 0');
      return;
    }
    try {
      const endpoint = selectedDebt.type === 'customer'
        ? `/customers/${selectedDebt.id}/pay-debt`
        : `/suppliers/${selectedDebt.id}/pay-debt`;
      const paymentData = { amount: debtPaymentAmount };
      if (currentStore?.id) {
        paymentData.store_id = currentStore.id;
      }
      await api.post(endpoint, paymentData);
      toast.success('Pagesa u regjistrua');
      setIsDebtPaymentModalOpen(false);
      setDebtPaymentAmount(0);
      loadAllData();
    } catch (error) {
      toast.error('Gabim në regjistrimin e pagesës');
    }
  };

  const handleManualDebtSubmit = async () => {
    if (!manualDebtForm.customer_id || manualDebtForm.amount <= 0) {
      toast.error('Plotësoni fushat e detyrueshme');
      return;
    }

    try {
      const customer = customers.find(c => c.id === manualDebtForm.customer_id);

      const saleData = {
        customer_id: customer.id,
        customer_name: customer.name,
        total: manualDebtForm.amount,
        paid: 0,
        debt: manualDebtForm.amount,
        items: [{
          product_id: 'manual_debt',
          product_name: manualDebtForm.notes || 'Borxh Manual',
          quantity: 1,
          price: manualDebtForm.amount,
          total: manualDebtForm.amount
        }],
        created_at: new Date(manualDebtForm.date + 'T12:00:00').toISOString(),
        notes: manualDebtForm.notes,
        store_id: currentStore?.id || ''
      };

      await api.post('/sales', saleData);
      toast.success('Borxhi u shtua manualisht');
      setIsManualDebtModalOpen(false);
      setManualDebtForm({ customer_id: '', amount: 0, date: new Date().toISOString().split('T')[0], notes: '' });
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleUpdateDebt = async () => {
    try {
      if (editDebtAmount < 0) {
        toast.error('Borxhi nuk mund të jetë negativ');
        return;
      }

      const endpoint = selectedDebt.type === 'customer'
        ? `/customers/${selectedDebt.id}`
        : `/suppliers/${selectedDebt.id}`;

      // We need to send required fields. Ideally fetching the current object first is safer,
      // but since we have the data in `customers` or `suppliers` state, we can find it.
      let updateData = {};

      if (selectedDebt.type === 'customer') {
        const customer = customers.find(c => c.id === selectedDebt.id);
        if (!customer) throw new Error('Klienti nuk u gjet');
        updateData = { ...customer, debt: editDebtAmount };
      } else {
        const supplier = suppliers.find(s => s.id === selectedDebt.id);
        if (!supplier) throw new Error('Furnitori nuk u gjet');
        updateData = { ...supplier, debt: editDebtAmount };
      }

      await api.put(endpoint, updateData);
      toast.success('Borxhi u përditësua');
      setIsEditDebtModalOpen(false);
      loadAllData();
    } catch (error) {
      console.error(error);
      toast.error('Gabim në përditësimin e borxhit');
    }
  };

  const handleDeleteDebt = async (debt) => {
    if (!window.confirm(`Jeni të sigurt që doni të fshini (anuloni) borxhin për ${debt.entity_name}?`)) return;

    try {
      const endpoint = debt.type === 'customer'
        ? `/customers/${debt.id}`
        : `/suppliers/${debt.id}`;

      let updateData = {};
      if (debt.type === 'customer') {
        const customer = customers.find(c => c.id === debt.id);
        if (!customer) return; // Should not happen
        updateData = { ...customer, debt: 0 };
      } else {
        const supplier = suppliers.find(s => s.id === debt.id);
        if (!supplier) return;
        updateData = { ...supplier, debt: 0 };
      }

      await api.put(endpoint, updateData);
      toast.success('Borxhi u fshi (u bë 0)');
      loadAllData();
    } catch (error) {
      console.error(error);
      toast.error('Gabim në fshirjen e borxhit');
    }
  };

  const exportDebtsToPDF = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lista e Borxheve</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 10px; }
            th { background: #f5f5f5; font-weight: bold; }
            .text-right { text-align: right; }
            .debt { color: #c00; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Lista e Borxheve</h1>
            <p>Eksportuar: ${new Date().toLocaleString('sq-AL')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Emri</th>
                <th>Lloji</th>
                <th>Telefoni</th>
                <th class="text-right">Borxhi</th>
              </tr>
            </thead>
            <tbody>
              ${filteredDebts.map(d => `
                <tr>
                  <td>${d.entity_name}</td>
                  <td>${d.type === 'customer' ? 'Klient' : 'Furnitor'}</td>
                  <td>${d.phone || '-'}</td>
                  <td class="text-right debt">${formatCurrency(d.debt)}</td>
                </tr>
              `).join('')}
              <tr style="font-weight:bold; background:#eee;">
                <td colspan="3">TOTAL</td>
                <td class="text-right debt">${formatCurrency(filteredDebts.reduce((s, d) => s + d.debt, 0))}</td>
              </tr>
            </tbody>
          </table>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const resetCustomerForm = () => {
    setCustomerForm({ name: '', phone: '', email: '', address: '', debt: 0 });
    setSelectedCustomer(null);
  };

  const openCustomerEdit = (customer) => {
    setSelectedCustomer(customer);
    setCustomerForm({ name: customer.name, phone: customer.phone || '', email: customer.email || '', address: customer.address || '', debt: customer.debt });
    setIsCustomerModalOpen(true);
  };

  const openCustomerWarranties = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await api.get(`/customers/${customer.id}/warranties`);
      setCustomerWarranties(response.data);
      setIsCustomerWarrantiesOpen(true);
    } catch (error) {
      toast.error('Gabim në ngarkimin e garancive');
    }
  };

  // Warranty handlers
  const handleWarrantySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...warrantyForm, end_date: calculateEndDate(warrantyForm.start_date, warrantyForm.duration_months) };
      if (currentStore?.id) {
        data.store_id = currentStore.id;
      }
      if (selectedWarranty) {
        await api.put(`/warranties/${selectedWarranty.id}`, data);
        toast.success('Garancia u përditësua');
      } else {
        await api.post('/warranties', data);
        toast.success('Garancia u shtua');
      }
      setIsWarrantyModalOpen(false);
      resetWarrantyForm();
      loadAllData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleWarrantyDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/warranties/${id}`);
      toast.success('Garancia u fshi');
      loadAllData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const resetWarrantyForm = () => {
    setWarrantyForm({
      type: 'product', category: 'smartphone', product_id: '', product_name: '', serial_number: '',
      customer_id: '', customer_name: '', start_date: new Date().toISOString().split('T')[0],
      end_date: '', duration_months: 12, notes: '', status: 'active',
    });
    setSelectedWarranty(null);
  };

  const openWarrantyEdit = (warranty) => {
    setSelectedWarranty(warranty);
    setWarrantyForm({
      type: warranty.type, category: warranty.category || 'smartphone',
      product_id: warranty.product_id || '', product_name: warranty.product_name,
      serial_number: warranty.serial_number || '', customer_id: warranty.customer_id || '',
      customer_name: warranty.customer_name, start_date: warranty.start_date.split('T')[0],
      end_date: warranty.end_date, duration_months: warranty.duration_months,
      notes: warranty.notes || '', status: warranty.status,
    });
    setIsWarrantyModalOpen(true);
  };

  const createNewCustomer = async () => {
    if (!newCustomerData.name.trim()) {
      toast.error('Emri është i detyrueshëm');
      return;
    }
    try {
      const customerData = { ...newCustomerData };
      if (currentStore?.id) {
        customerData.store_id = currentStore.id;
      }
      const response = await api.post('/customers', customerData);
      const newCustomer = response.data;
      setCustomers([...customers, newCustomer]);
      setWarrantyForm({ ...warrantyForm, customer_id: newCustomer.id, customer_name: newCustomer.name });
      setIsNewCustomerOpen(false);
      setNewCustomerData({ name: '', phone: '', address: '' });
      toast.success('Klienti u krijua');
    } catch (error) {
      toast.error('Gabim në krijimin e klientit');
    }
  };

  // Template handlers
  const handleTemplateSave = async () => {
    if (!currentStore?.id) return;

    const templates = [...warrantyTemplates];
    const existingIndex = templates.findIndex(t => t.category === templateForm.category);

    if (existingIndex >= 0) {
      templates[existingIndex] = { ...templateForm };
    } else {
      templates.push({ ...templateForm });
    }

    try {
      const updateData = {
        ...currentStore,
        warranty_templates: templates
      };
      await api.put(`/stores/${currentStore.id}`, updateData);
      setWarrantyTemplates(templates); // Optimistic
      toast.success('Shablloni u ruajt');
      setIsTemplateModalOpen(false);
      refreshStores();
    } catch (error) {
      console.error(error);
      toast.error('Gabim në ruajtjen e shabllonit');
    }
  };

  const handleTemplateDelete = async (category) => {
    if (!currentStore?.id) return;
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë shabllon?')) return;

    const templates = warrantyTemplates.filter(t => t.category !== category);

    try {
      const updateData = {
        ...currentStore,
        warranty_templates: templates
      };
      await api.put(`/stores/${currentStore.id}`, updateData);
      setWarrantyTemplates(templates);
      toast.success('Shablloni u fshi');
      refreshStores();
    } catch (error) {
      console.error(error);
      toast.error('Gabim në fshirjen e shabllonit');
    }
  };

  const handleWarrantyLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setTemplateForm(prev => ({ ...prev, logo_url: event.target.result }));
      toast.success('Logo u ngarkua');
    };
    reader.readAsDataURL(file);
  };

  const openTemplateEdit = (category) => {
    const existing = warrantyTemplates.find(t => t.category === category);
    const defaultTerms = DEFAULT_WARRANTY_TERMS[category] || DEFAULT_WARRANTY_TERMS.default;
    const catInfo = WARRANTY_CATEGORIES.find(c => c.value === category);

    if (existing) {
      setTemplateForm({
        ...existing,
        logo_url: existing.logo_url || '',
        store_name: existing.store_name || 'MIRI SHOP'
      });
    } else {
      setTemplateForm({
        category,
        name: catInfo?.label || category,
        duration_months: catInfo?.defaultMonths || 12,
        covered_terms: defaultTerms.covered.join('\n'),
        action_terms: defaultTerms.actions.join('\n'),
        excluded_terms: defaultTerms.excluded.join('\n'),
        logo_url: '',
        store_name: 'MIRI SHOP'
      });
    }
    setIsTemplateModalOpen(true);
  };

  // Print warranty
  const printWarranty = (warranty) => {
    // 1. Get Global Settings from currentStore
    const settings = {
      primary_color: currentStore?.warranty_primary_color || '#E52B50',
      font_family: currentStore?.warranty_font_family || 'Arial',
      show_logo: currentStore?.warranty_show_logo !== false,
      show_terms: currentStore?.warranty_show_terms !== false,
      footer_text: currentStore?.warranty_footer_text || 'Ruajeni këtë kartë dhe faturën origjinale',
      covered_terms: currentStore?.warranty_covered_terms || '',
      excluded_terms: currentStore?.warranty_excluded_terms || '',
      logo_url: currentStore?.logo_url || '',
      store_name: currentStore?.name || 'MIRI SHOP',
      phone: currentStore?.phone || ''
    };

    // 2. Find Category & Template Info
    const category = warranty.category || 'other';
    const template = warrantyTemplates.find(t => t.category === category);

    // 3. Resolve Terms
    const parseTerms = (str) => str ? str.split('\\n').filter(t => t.trim()) : [];
    const defaultCatTerms = DEFAULT_WARRANTY_TERMS[category] || DEFAULT_WARRANTY_TERMS.default;

    const covered = template?.covered_terms
      ? parseTerms(template.covered_terms)
      : (parseTerms(settings.covered_terms).length > 0 ? parseTerms(settings.covered_terms) : defaultCatTerms.covered);

    const excluded = template?.excluded_terms
      ? parseTerms(template.excluded_terms)
      : (parseTerms(settings.excluded_terms).length > 0 ? parseTerms(settings.excluded_terms) : defaultCatTerms.excluded);

    // Get logo and store name
    const logoUrl = (settings.show_logo && (template?.logo_url || settings.logo_url)) || '';
    const storeName = template?.store_name || settings.store_name;
    const storePhone = settings.phone;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kartë Garancie - ${warranty.customer_name}</title>
          <style>
            @page { margin: 10mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: ${settings.font_family}, sans-serif; padding: 20px; background: #f5f5f5; }
            .card {
              background: white;
              border: 4px solid ${settings.primary_color};
              border-radius: 16px;
              padding: 30px;
              max-width: 450px;
              margin: auto;
              box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid ${settings.primary_color};
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            .header .logo { max-width: 60px; max-height: 60px; margin-bottom: 10px; }
            .header h1 { color: ${settings.primary_color}; font-size: 28px; margin-bottom: 5px; }
            .header .store-name { color: #666; font-size: 14px; }
            .info { margin: 20px 0; }
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              padding: 12px 0;
              border-bottom: 1px solid #eee;
              font-size: 14px;
            }
            .info-row span:first-child { color: #666; }
            .info-row span:last-child { font-weight: bold; color: #333; }
            .validity {
              background: ${settings.primary_color};
              color: white;
              padding: 25px;
              border-radius: 12px;
              text-align: center;
              margin: 25px 0;
            }
            .validity h3 { font-size: 28px; margin-bottom: 8px; }
            .validity p { opacity: 0.9; }
            .terms { 
              font-size: 12px; 
              margin-top: 20px; 
              padding: 20px; 
              background: #f9f9f9; 
              border-radius: 10px; 
            }
            .terms h4 { color: ${settings.primary_color}; margin-bottom: 10px; }
            .terms .excluded h4 { color: #dc2626; }
            .terms ul { margin-left: 20px; margin-bottom: 15px; }
            .terms li { margin: 6px 0; color: #555; }
            .footer { 
              text-align: center; 
              margin-top: 25px; 
              padding-top: 20px;
              border-top: 2px dashed ${settings.primary_color};
              color: #666;
              font-size: 13px;
            }
            .footer p { margin: 5px 0; }
            .footer .phone { color: ${settings.primary_color}; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header">
              ${logoUrl ? `<img src="${logoUrl}" class="logo" alt="Logo" />` : ''}
              <h1>KARTË GARANCIE</h1>
              <p class="store-name">${storeName}</p>
            </div>
            
            <div class="info">
              <div class="info-row">
                <span>Nr. Garancie:</span>
                 <span>${warranty.id?.slice(0, 8).toUpperCase()}</span>
              </div>
              <div class="info-row">
                <span>Klienti:</span>
                <span>${warranty.customer_name}</span>
              </div>
              <div class="info-row">
                <span>Data Blerjes:</span>
                <span>${formatDate(warranty.start_date)}</span>
              </div>
              <div class="info-row">
                <span>Produkti:</span>
                <span>${warranty.product_name}</span>
              </div>
               ${warranty.serial_number ? `
              <div class="info-row">
                <span>Serial / IMEI:</span>
                <span>${warranty.serial_number}</span>
              </div>
              ` : ''}
            </div>
            
            <div class="validity">
              <h3>${warranty.duration_months} MUAJ GARANCI</h3>
              <p>Vlefshmëria: deri më ${formatDate(warranty.end_date)}</p>
            </div>
            
            ${settings.show_terms ? `
              <div class="terms">
                <h4>Kushtet që mbulohen:</h4>
                <ul>
                  ${covered.map(t => `<li>${t}</li>`).join('')}
                </ul>
                <div class="excluded">
                  <h4>Kushtet që NUK mbulohen:</h4>
                  <ul>
                    ${excluded.map(t => `<li>${t}</li>`).join('')}
                  </ul>
                </div>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>${settings.footer_text}</p>
              ${storePhone ? `<p class="phone">Tel: ${storePhone}</p>` : ''}
            </div>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const printCustomerWarranties = () => {
    // Get invoice settings from current store or use defaults
    const invoiceSettings = {
      font_family: currentStore?.invoice_font_family || 'Arial',
      primary_color: currentStore?.invoice_primary_color || '#E52B50',
      show_logo: currentStore?.invoice_show_logo !== false,
      logo_url: currentStore?.logo_url || '',
      store_name: currentStore?.name || 'MIRI SHOP',
      header_text: currentStore?.invoice_header || '',
      footer_text: currentStore?.invoice_footer || 'Faleminderit per besimin!',
      address: currentStore?.address || '',
      phone: currentStore?.phone || '',
      nipt: currentStore?.nipt || ''
    };

    const printWindow = window.open('', '_blank');
    const warrantyRows = customerWarranties.map(w => `
      <tr style="border-bottom: 1px solid #eee;">
        <td style="padding: 12px 8px;">${w.product_name}</td>
        <td style="padding: 12px 8px;">${w.type === 'product' ? 'Produkt' : 'Servis'}</td>
        <td style="padding: 12px 8px;">${formatDate(w.start_date)}</td>
        <td style="padding: 12px 8px;">${formatDate(w.end_date)}</td>
        <td style="padding: 12px 8px;">${w.duration_months} muaj</td>
        <td style="padding: 12px 8px;">
          <span style="padding: 4px 8px; border-radius: 99px; font-size: 11px; background: ${w.status === 'active' ? '#dcfce7' : '#f1f5f9'}; color: ${w.status === 'active' ? '#166534' : '#64748b'};">
            ${w.status === 'active' ? 'Aktive' : 'Skaduar'}
          </span>
        </td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Garancitë - ${selectedCustomer?.name}</title>
        <style>
          body { font-family: '${invoiceSettings.font_family}', sans-serif; padding: 40px; color: #333; }
          h1 { color: ${invoiceSettings.primary_color}; margin-bottom: 5px; text-transform: uppercase; letter-spacing: -0.5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 30px; font-size: 13px; }
          th { background-color: ${invoiceSettings.primary_color}; color: white; padding: 12px 10px; text-align: left; font-weight: 500; font-size: 12px; text-transform: uppercase; }
          td { color: #444; }
          .header { border-bottom: 2px solid ${invoiceSettings.primary_color}; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
          .store-info h1 { margin: 0; font-size: 24px; }
          .store-meta { font-size: 12px; color: #666; margin-top: 5px; }
          .customer-info { text-align: right; }
          .logo { max-height: 60px; margin-bottom: 10px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; font-size: 12px; color: #888; }
          .print-btn { display: none; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="store-info">
            ${invoiceSettings.show_logo && invoiceSettings.logo_url ? `<img src="${invoiceSettings.logo_url}" class="logo" />` : ''}
            <h1 style="color: ${invoiceSettings.primary_color}">${invoiceSettings.store_name}</h1>
            <div class="store-meta">
              ${invoiceSettings.address ? `<span>${invoiceSettings.address}</span>` : ''}
              ${invoiceSettings.address && invoiceSettings.phone ? ' • ' : ''}
              ${invoiceSettings.phone ? `<span>Tel: ${invoiceSettings.phone}</span>` : ''}
              ${(invoiceSettings.address || invoiceSettings.phone) && invoiceSettings.nipt ? ' • ' : ''}
              ${invoiceSettings.nipt ? `<span>NIPT: ${invoiceSettings.nipt}</span>` : ''}
            </div>
            ${invoiceSettings.header_text ? `<p style="font-size: 13px; margin-top: 10px; font-style: italic;">${invoiceSettings.header_text}</p>` : ''}
          </div>
          <div class="customer-info">
            <p style="font-size: 12px; color: #888; margin-bottom: 5px;">PASQYRA E GARANCIVE PËR:</p>
            <h2 style="margin: 0; font-size: 20px;">${selectedCustomer?.name}</h2>
            ${selectedCustomer?.phone ? `<p style="margin: 5px 0 0 0; color: #666;">Tel: ${selectedCustomer.phone}</p>` : ''}
            <p style="margin: 5px 0 0 0; font-size: 12px; color: #aaa;">Data: ${new Date().toLocaleDateString('sq-AL')}</p>
          </div>
        </div>

        <table>
          <thead><tr><th>Produkti</th><th>Lloji</th><th>Fillimi</th><th>Skadimi</th><th>Kohëzgjatja</th><th>Statusi</th></tr></thead>
          <tbody>${warrantyRows || '<tr><td colspan="6" style="text-align: center; padding: 20px;">Nuk ka garanci të regjistruara</td></tr>'}</tbody>
        </table>

        <div class="footer">
          <p>${invoiceSettings.footer_text}</p>
        </div>

        <script>
          window.onload = function() {
              if (document.images.length > 0) {
                var img = document.images[0];
                if (img.complete) window.print();
                else img.onload = function() { window.print(); };
              } else {
                window.print();
              }
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getStatusBadge = (warranty) => {
    if (warranty.status === 'expired' || isExpired(warranty.end_date)) {
      return <Badge variant="destructive">Skaduar</Badge>;
    }
    if (isExpiringSoon(warranty.end_date)) {
      return <Badge variant="warning" className="bg-orange-100 text-orange-800">Skadon së shpejti</Badge>;
    }
    return <Badge variant="success" className="bg-lime-100 text-lime-800">Aktive</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="klientet-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className={`grid w-full max-w-2xl ${showWarrantyTabs ? 'grid-cols-5' : 'grid-cols-3'}`}>
          <TabsTrigger value="customers"><Users className="w-4 h-4 mr-2" />Klientët</TabsTrigger>
          <TabsTrigger value="sales"><ShoppingCart className="w-4 h-4 mr-2" />Shitjet</TabsTrigger>
          {showWarrantyTabs && <TabsTrigger value="warranties"><Shield className="w-4 h-4 mr-2" />Garancitë</TabsTrigger>}
          <TabsTrigger value="debts"><CreditCard className="w-4 h-4 mr-2" />Borxhet</TabsTrigger>
          {showWarrantyTabs && <TabsTrigger value="templates"><Settings className="w-4 h-4 mr-2" />Shabllone</TabsTrigger>}
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Kërko klientë..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="pl-10" />
            </div>
            <Button onClick={() => { resetCustomerForm(); setIsCustomerModalOpen(true); }}><Plus className="w-4 h-4 mr-2" />Klient i Ri</Button>
          </div>

          {totalCustomerDebt > 0 && (
            <Card className="border-rose-200 bg-rose-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <span className="text-rose-800 font-medium">Borxh total nga klientët:</span>
                  <span className="text-rose-800 font-mono font-bold">{formatCurrency(totalCustomerDebt)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showStoreColumn && <TableHead>Dyqani</TableHead>}
                    <TableHead>Emri</TableHead>
                    <TableHead>Telefoni</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Adresa</TableHead>
                    <TableHead className="text-right">Borxhi</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow><TableCell colSpan={showStoreColumn ? 7 : 6} className="text-center py-8 text-slate-500"><Users className="w-12 h-12 mx-auto mb-2 opacity-20" />Nuk u gjet asnjë klient</TableCell></TableRow>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        {showStoreColumn && <TableCell><Badge variant="outline" className="text-xs">{getStoreName(customer.store_id)}</Badge></TableCell>}
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>{customer.phone && <span className="flex items-center gap-1 text-sm"><Phone className="w-3 h-3" />{customer.phone}</span>}</TableCell>
                        <TableCell>{customer.email && <span className="flex items-center gap-1 text-sm"><Mail className="w-3 h-3" />{customer.email}</span>}</TableCell>
                        <TableCell>{customer.address && <span className="flex items-center gap-1 text-sm"><MapPin className="w-3 h-3" />{customer.address}</span>}</TableCell>
                        <TableCell className="text-right">{customer.debt > 0 ? <Badge variant="destructive" className="font-mono">{formatCurrency(customer.debt)}</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openCustomerWarranties(customer)} title="Garancitë"><FileText className="w-4 h-4 text-blue-600" /></Button>
                            {customer.debt > 0 && <Button variant="ghost" size="sm" onClick={() => { setSelectedCustomer(customer); setPaymentAmount(customer.debt); setIsPaymentModalOpen(true); }}><CreditCard className="w-4 h-4 text-lime-600" /></Button>}
                            <Button variant="ghost" size="sm" onClick={() => openCustomerEdit(customer)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleCustomerDelete(customer.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{sales.length}</p>
                <p className="text-sm text-slate-500">Total Shitje</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalSalesRevenue)}</p>
                <p className="text-sm text-slate-500">Të ardhura</p>
              </CardContent>
            </Card>
            <Card className="border-rose-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-rose-600">{formatCurrency(totalSalesDebt)}</p>
                <p className="text-sm text-slate-500">Borxhe Shitjesh</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{sales.filter(s => s.debt > 0).length}</p>
                <p className="text-sm text-slate-500">Me Borxh</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Kërko shitje..." value={salesSearch} onChange={(e) => setSalesSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={salesCustomerFilter} onValueChange={setSalesCustomerFilter}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Të gjithë klientët" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Të gjithë klientët</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportToExcel} title="Eksporto Excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={exportToPDF} title="Eksporto PDF">
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>

          {/* Sales Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showStoreColumn && <TableHead>Dyqani</TableHead>}
                    <TableHead>Data</TableHead>
                    <TableHead>Klienti</TableHead>
                    <TableHead>Artikuj</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right text-center">Metoda</TableHead>
                    <TableHead className="text-right">Paguar</TableHead>
                    <TableHead className="text-right">Borxh</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showStoreColumn ? 8 : 7} className="text-center py-8 text-slate-500">
                        <Receipt className="w-12 h-12 mx-auto mb-2 opacity-20" />
                        Nuk ka shitje
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSales.map((sale) => (
                      <TableRow key={sale.id}>
                        {showStoreColumn && <TableCell><Badge variant="outline" className="text-xs">{getStoreName(sale.store_id)}</Badge></TableCell>}
                        <TableCell className="font-mono text-xs">{formatDateTime(sale.created_at)}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{sale.customer_name}</p>
                            <p className="text-xs text-slate-500">#{sale.id?.slice(0, 8)}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[200px]">
                          {sale.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold">{formatCurrency(sale.total)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary" className={`text-xs ${sale.payment_method === 'card' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                            {sale.payment_method === 'card' ? <><CreditCard className="w-3 h-3 mr-1" />Card</> : <><Receipt className="w-3 h-3 mr-1" />Cash</>}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-600">{formatCurrency(sale.paid)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {sale.debt > 0 ? (
                            <Badge variant="destructive">{formatCurrency(sale.debt)}</Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600">Paguar</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => printSaleReceipt(sale)} title="Printo">
                              <Printer className="w-4 h-4 text-green-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setEditingSale(sale); setEditPaid(sale.paid); setEditPaymentMethod(sale.payment_method || 'cash'); setIsEditSaleOpen(true); }} title="Modifiko">
                              <Edit className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSale(sale)} title="Fshi">
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Warranties Tab */}
        <TabsContent value="warranties" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Kërko garanci..." value={warrantySearch} onChange={(e) => setWarrantySearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40"><SelectValue placeholder="Lloji" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Të gjitha</SelectItem>
                  <SelectItem value="product">Produkte</SelectItem>
                  <SelectItem value="service">Servise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { resetWarrantyForm(); setIsWarrantyModalOpen(true); }}><Plus className="w-4 h-4 mr-2" />Garanci e Re</Button>
          </div>

          {expiringWarranties.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="py-3">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">{expiringWarranties.length} garanci skadojnë brenda 30 ditëve</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showStoreColumn && <TableHead>Dyqani</TableHead>}
                    <TableHead>Kategoria</TableHead>
                    <TableHead>Produkti/Pajisja</TableHead>
                    <TableHead>Klienti</TableHead>
                    <TableHead>Skadimi</TableHead>
                    <TableHead>Kohëzgjatja</TableHead>
                    <TableHead>Statusi</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWarranties.length === 0 ? (
                    <TableRow><TableCell colSpan={showStoreColumn ? 8 : 7} className="text-center py-8 text-slate-500"><Shield className="w-12 h-12 mx-auto mb-2 opacity-20" />Nuk u gjet asnjë garanci</TableCell></TableRow>
                  ) : (
                    filteredWarranties.map((warranty) => (
                      <TableRow key={warranty.id}>
                        {showStoreColumn && <TableCell><Badge variant="outline" className="text-xs">{getStoreName(warranty.store_id)}</Badge></TableCell>}
                        <TableCell><Badge variant="outline">{WARRANTY_CATEGORIES.find(c => c.value === warranty.category)?.label || warranty.type}</Badge></TableCell>
                        <TableCell><div><p className="font-medium">{warranty.product_name}</p>{warranty.serial_number && <p className="text-xs text-slate-500">{warranty.serial_number}</p>}</div></TableCell>
                        <TableCell>{warranty.customer_name}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(warranty.end_date)}</TableCell>
                        <TableCell className="font-mono">{warranty.duration_months}m</TableCell>
                        <TableCell>{getStatusBadge(warranty)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => printWarranty(warranty)}><Printer className="w-4 h-4 text-blue-500" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openWarrantyEdit(warranty)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleWarrantyDelete(warranty.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" />Shabllone Garancish</CardTitle>
                  <CardDescription>Personalizo kushtet e garancisë për secilën kategori produkti</CardDescription>
                </div>
                <Button onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateForm({
                    category: 'custom',
                    store_name: currentStore?.name || 'DYQANI',
                    duration_months: 12,
                    covered_terms: '',
                    action_terms: '',
                    excluded_terms: '',
                    logo_url: ''
                  });
                  setIsTemplateModalOpen(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />Shabllone e Re
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {WARRANTY_CATEGORIES.map((cat) => {
                  const template = warrantyTemplates.find(t => t.category === cat.value);
                  return (
                    <Card key={cat.value} className={`hover:border-primary transition-colors ${template ? 'border-lime-300 bg-lime-50' : ''}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="cursor-pointer flex-1" onClick={() => openTemplateEdit(cat.value)}>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{cat.label}</p>
                              {template && <Badge variant="outline" className="text-xs bg-lime-100 text-lime-700 border-lime-300">Personalizuar</Badge>}
                            </div>
                            <p className="text-sm text-slate-500">{template?.duration_months || cat.defaultMonths} muaj garanci</p>
                            {template?.store_name && (
                              <p className="text-xs text-slate-400 mt-1">{template.store_name}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openTemplateEdit(cat.value)} title="Modifiko">
                              <Edit className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (template) {
                                  handleTemplateDelete(cat.value);
                                } else {
                                  toast.info('Nuk ka shabllone të ruajtur për të fshirë. Së pari personalizoni shabllonin.');
                                }
                              }}
                              title="Fshi shabllonin"
                              data-testid={`delete-template-${cat.value}`}
                            >
                              <Trash2 className={`w-4 h-4 ${template ? 'text-rose-500 hover:text-rose-600' : 'text-rose-300 hover:text-rose-400'}`} />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Custom Templates */}
                {warrantyTemplates.filter(t => !WARRANTY_CATEGORIES.find(c => c.value === t.category)).map((template) => (
                  <Card key={template.category} className="border-violet-300 bg-violet-50 hover:border-violet-400 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="cursor-pointer flex-1" onClick={() => openTemplateEdit(template.category)}>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{template.category}</p>
                            <Badge variant="secondary" className="text-xs">Custom</Badge>
                          </div>
                          <p className="text-sm text-slate-500">{template.duration_months} muaj garanci</p>
                          {template.store_name && (
                            <p className="text-xs text-slate-400 mt-1">{template.store_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openTemplateEdit(template.category)} title="Modifiko">
                            <Edit className="w-4 h-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTemplateDelete(template.category)}
                            title="Fshi shabllonin"
                            data-testid={`delete-template-${template.category}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500 hover:text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debts Tab */}
        <TabsContent value="debts" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className={totalCustomerDebt > 0 ? 'border-rose-200' : ''}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Borxh nga Klientët</p>
                    <p className="text-2xl font-bold font-mono text-rose-600">{formatCurrency(totalCustomerDebt)}</p>
                  </div>
                  <Users className="w-8 h-8 text-rose-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className={totalSupplierDebt > 0 ? 'border-orange-200' : ''}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Borxh ndaj Furnitorëve</p>
                    <p className="text-2xl font-bold font-mono text-orange-600">{formatCurrency(totalSupplierDebt)}</p>
                  </div>
                  <Truck className="w-8 h-8 text-orange-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Bilanci Neto</p>
                    <p className={`text-2xl font-bold font-mono ${totalCustomerDebt - totalSupplierDebt >= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                      {formatCurrency(totalCustomerDebt - totalSupplierDebt)}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 text-slate-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Kërko..." value={debtSearch} onChange={(e) => setDebtSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={debtFilter} onValueChange={setDebtFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjithë</SelectItem>
                <SelectItem value="customers">Klientët</SelectItem>
                <SelectItem value="suppliers">Furnitorët</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button onClick={() => setIsManualDebtModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />Shto Borxh
              </Button>
              <Button variant="outline" onClick={exportDebtsToPDF} title="Eksporto PDF">
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showStoreColumn && <TableHead>Dyqani</TableHead>}
                    <TableHead>Lloji</TableHead>
                    <TableHead>Emri</TableHead>
                    <TableHead>Telefoni</TableHead>
                    <TableHead className="text-right">Borxhi</TableHead>
                    <TableHead className="text-right">Veprime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDebts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showStoreColumn ? 6 : 5} className="text-center py-8 text-slate-500">
                        <Check className="w-12 h-12 mx-auto mb-2 text-lime-500" />
                        Nuk ka borxhe të regjistruara
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDebts.map((debt) => (
                      <TableRow key={`${debt.type}-${debt.id}`}>
                        {showStoreColumn && <TableCell><Badge variant="outline" className="text-xs">{getStoreName(debt.store_id)}</Badge></TableCell>}
                        <TableCell>
                          <Badge variant={debt.type === 'customer' ? 'destructive' : 'warning'} className={debt.type === 'supplier' ? 'bg-orange-100 text-orange-800' : ''}>
                            {debt.type === 'customer' ? 'Klient' : 'Furnitor'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{debt.entity_name}</TableCell>
                        <TableCell>{debt.phone && <span className="flex items-center gap-1 text-sm"><Phone className="w-3 h-3" />{debt.phone}</span>}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-bold ${debt.type === 'customer' ? 'text-rose-600' : 'text-orange-600'}`}>
                            {formatCurrency(debt.debt)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedDebt(debt); setDebtPaymentAmount(debt.debt); setIsDebtPaymentModalOpen(true); }}
                              title="Paguaj"
                            >
                              <CreditCard className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedDebt(debt); setEditDebtAmount(debt.debt); setIsEditDebtModalOpen(true); }}
                              title="Modifiko Manualisht"
                            >
                              <Edit className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDebt(debt)}
                              title="Fshi (Hiq borxhin)"
                            >
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Customer Modal */}
      <Dialog open={isCustomerModalOpen} onOpenChange={setIsCustomerModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedCustomer ? 'Modifiko Klientin' : 'Klient i Ri'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCustomerSubmit} className="space-y-4">
            <div><Label>Emri *</Label><Input value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} required /></div>
            <div><Label>Telefoni</Label><Input value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} /></div>
            <div><Label>Adresa</Label><Input value={customerForm.address} onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsCustomerModalOpen(false)}>Anulo</Button><Button type="submit">Ruaj</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Warranty Modal */}
      <Dialog open={isWarrantyModalOpen} onOpenChange={setIsWarrantyModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selectedWarranty ? 'Modifiko Garancinë' : 'Garanci e Re'}</DialogTitle></DialogHeader>
          <form onSubmit={handleWarrantySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Lloji</Label>
                <Select value={warrantyForm.type} onValueChange={(v) => setWarrantyForm({ ...warrantyForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="product">Produkt</SelectItem><SelectItem value="service">Servis</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Kategoria</Label>
                <Select value={warrantyForm.category} onValueChange={(v) => {
                  // Merge default categories with any custom ones in templates
                  const allCategories = [...WARRANTY_CATEGORIES];
                  warrantyTemplates.forEach(t => {
                    if (!allCategories.find(c => c.value === t.category)) {
                      allCategories.push({ value: t.category, label: t.category, defaultMonths: t.duration_months });
                    }
                  });

                  const cat = allCategories.find(c => c.value === v);
                  const template = warrantyTemplates.find(t => t.category === v);

                  // Auto-apply duration from template or category default
                  setWarrantyForm({
                    ...warrantyForm,
                    category: v,
                    duration_months: template?.duration_months || cat?.defaultMonths || 12
                  });
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {/* Combine default and custom categories for the dropdown */}
                    {[...WARRANTY_CATEGORIES, ...warrantyTemplates.filter(t => !WARRANTY_CATEGORIES.find(c => c.value === t.category)).map(t => ({
                      value: t.category, label: t.category
                    }))].map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {warrantyForm.type === 'product' ? (
              <div><Label>Produkti</Label>
                <Select value={warrantyForm.product_id} onValueChange={(v) => { const p = products.find(x => x.id === v); setWarrantyForm({ ...warrantyForm, product_id: v, product_name: p?.name || '' }); }}>
                  <SelectTrigger><SelectValue placeholder="Zgjidh produktin" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label>Emri i produktit/servisit</Label><Input value={warrantyForm.product_name} onChange={(e) => setWarrantyForm({ ...warrantyForm, product_name: e.target.value })} required /></div>
            )}
            <div><Label>Serial/IMEI</Label><Input value={warrantyForm.serial_number} onChange={(e) => setWarrantyForm({ ...warrantyForm, serial_number: e.target.value })} placeholder="Opsionale" /></div>
            <div><Label>Klienti</Label>
              <Select value={warrantyForm.customer_id} onValueChange={(v) => {
                if (v === 'new') { setIsNewCustomerOpen(true); }
                else { const c = customers.find(x => x.id === v); setWarrantyForm({ ...warrantyForm, customer_id: v, customer_name: c?.name || '' }); }
              }}>
                <SelectTrigger><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new" className="text-primary font-medium">+ Klient i ri</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Data Fillimit</Label><Input type="date" value={warrantyForm.start_date} onChange={(e) => setWarrantyForm({ ...warrantyForm, start_date: e.target.value })} required /></div>
              <div><Label>Kohëzgjatja (muaj)</Label><NumericInput value={warrantyForm.duration_months} onChange={(e) => setWarrantyForm({ ...warrantyForm, duration_months: parseInt(e.target.value) || 0 })} min={1} /></div>
            </div>
            <div><Label>Shënime</Label><Input value={warrantyForm.notes} onChange={(e) => setWarrantyForm({ ...warrantyForm, notes: e.target.value })} /></div>
            <div><Label>Statusi</Label>
              <Select value={warrantyForm.status} onValueChange={(v) => setWarrantyForm({ ...warrantyForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Aktive</SelectItem><SelectItem value="expired">Skaduar</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setIsWarrantyModalOpen(false)}>Anulo</Button><Button type="submit">Ruaj</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pagesë Borxhi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-slate-500">Klienti</p>
              <p className="text-xl font-semibold">{selectedCustomer?.name}</p>
              <p className="text-slate-500 mt-2">Borxhi aktual</p>
              <p className="text-2xl font-mono font-bold text-rose-600">{formatCurrency(selectedCustomer?.debt || 0)}</p>
            </div>
            <div><Label>Shuma e pagesës (LEK)</Label><NumericInput value={paymentAmount} onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)} className="font-mono text-lg" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>Anulo</Button><Button onClick={handlePayment}>Regjistro Pagesën</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Warranties Modal */}
      <Dialog open={isCustomerWarrantiesOpen} onOpenChange={setIsCustomerWarrantiesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#E52B50]" />Garancitë - {selectedCustomer?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {customerWarranties.length === 0 ? (
              <div className="text-center py-8 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-2 opacity-20" /><p>Ky klient nuk ka garanci të regjistruara</p></div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Produkti</TableHead><TableHead>Lloji</TableHead><TableHead>Skadimi</TableHead><TableHead>Statusi</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {customerWarranties.map((w) => (
                      <TableRow key={w.id}>
                        <TableCell className="font-medium">{w.product_name}</TableCell>
                        <TableCell><Badge variant="outline">{w.type === 'product' ? 'Produkt' : 'Servis'}</Badge></TableCell>
                        <TableCell>{formatDate(w.end_date)}</TableCell>
                        <TableCell><Badge variant={w.status === 'active' ? 'default' : 'secondary'}>{w.status === 'active' ? 'Aktive' : 'Skaduar'}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCustomerWarrantiesOpen(false)}>Mbyll</Button>
            {customerWarranties.length > 0 && <Button onClick={printCustomerWarranties}><Printer className="w-4 h-4 mr-2" />Printo</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Customer Modal */}
      <Dialog open={isNewCustomerOpen} onOpenChange={setIsNewCustomerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Klient i Ri</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Emri *</Label><Input value={newCustomerData.name} onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })} /></div>
            <div><Label>Telefon</Label><Input value={newCustomerData.phone} onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })} /></div>
            <div><Label>Adresa</Label><Input value={newCustomerData.address} onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsNewCustomerOpen(false)}>Anulo</Button><Button onClick={createNewCustomer}>Krijo</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit Modal */}
      <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {templateForm.category === 'custom'
                ? 'Krijo Shabllone të Re'
                : `Shablloni i Garancisë - ${WARRANTY_CATEGORIES.find(c => c.value === templateForm.category)?.label || templateForm.category}`}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-600">Informacioni i Dyqanit</h3>

              {/* Category name for custom templates */}
              {!WARRANTY_CATEGORIES.find(c => c.value === templateForm.category) && (
                <div>
                  <Label>Emri i Kategorisë/Shabllonit</Label>
                  <Input
                    value={templateForm.category === 'custom' ? '' : templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    placeholder="p.sh. Elektronikë Premium, Veshje Luksoze..."
                  />
                </div>
              )}

              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Logo e Garancisë</Label>
                <div className="flex items-center gap-3">
                  {templateForm.logo_url ? (
                    <img src={templateForm.logo_url} alt="Logo" className="w-16 h-16 object-contain border rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 border rounded flex items-center justify-center">
                      <Shield className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="file" accept="image/*" onChange={handleWarrantyLogoUpload} className="hidden" id="warranty-logo" />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('warranty-logo')?.click()}>
                      Ngarko Logo
                    </Button>
                    {templateForm.logo_url && (
                      <Button variant="ghost" size="sm" onClick={() => setTemplateForm(p => ({ ...p, logo_url: '' }))}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>Emri i Dyqanit</Label>
                <Input value={templateForm.store_name} onChange={(e) => setTemplateForm({ ...templateForm, store_name: e.target.value })} placeholder="MIRI SHOP" />
              </div>

              <div>
                <Label>Kohëzgjatja Default (muaj)</Label>
                <NumericInput value={templateForm.duration_months} onChange={(e) => setTemplateForm({ ...templateForm, duration_months: parseInt(e.target.value) || 12 })} />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-600">Kushtet e Garancisë</h3>
              <div><Label>Kushtet e Mbulimit (një per rresht)</Label><Textarea value={templateForm.covered_terms} onChange={(e) => setTemplateForm({ ...templateForm, covered_terms: e.target.value })} rows={3} placeholder="Shkruaj kushtet që mbulohen..." /></div>
              <div><Label>Veprimet e Garancisë (një per rresht)</Label><Textarea value={templateForm.action_terms} onChange={(e) => setTemplateForm({ ...templateForm, action_terms: e.target.value })} rows={3} placeholder="Çfarë bëjmë për garancinë..." /></div>
              <div><Label>Kushtet që NUK Mbulohen (një per rresht)</Label><Textarea value={templateForm.excluded_terms} onChange={(e) => setTemplateForm({ ...templateForm, excluded_terms: e.target.value })} rows={3} placeholder="Çfarë NUK mbulohet..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateModalOpen(false)}>Anulo</Button>
            <Button onClick={handleTemplateSave} disabled={!templateForm.category || templateForm.category === 'custom'}>
              Ruaj Shabllonin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Debt Payment Modal */}
      <Dialog open={isDebtPaymentModalOpen} onOpenChange={setIsDebtPaymentModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pagesë Borxhi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4">
              <Badge variant={selectedDebt?.type === 'customer' ? 'destructive' : 'warning'} className={`mb-2 ${selectedDebt?.type === 'supplier' ? 'bg-orange-100 text-orange-800' : ''}`}>
                {selectedDebt?.type === 'customer' ? 'Klient' : 'Furnitor'}
              </Badge>
              <p className="text-xl font-semibold">{selectedDebt?.entity_name}</p>
              <p className="text-slate-500 mt-2">Borxhi aktual</p>
              <p className={`text-2xl font-mono font-bold ${selectedDebt?.type === 'customer' ? 'text-rose-600' : 'text-orange-600'}`}>
                {formatCurrency(selectedDebt?.debt || 0)}
              </p>
            </div>
            <div>
              <Label>Shuma e pagesës (LEK)</Label>
              <NumericInput
                value={debtPaymentAmount}
                onChange={(e) => setDebtPaymentAmount(parseFloat(e.target.value) || 0)}
                max={selectedDebt?.debt || 0}
                className="font-mono text-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDebtPaymentModalOpen(false)}>Anulo</Button>
            <Button onClick={handleDebtPayment}>Regjistro Pagesën</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={isEditSaleOpen} onOpenChange={setIsEditSaleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifiko Shitjen</DialogTitle>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Klienti:</p>
                <p className="font-semibold">{editingSale.customer_name}</p>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Artikujt:</p>
                {editingSale.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span className="font-mono">{formatCurrency(item.total)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                <span className="font-medium">Total:</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(editingSale.total)}</span>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Metoda e Pagesës</Label>
                  <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash (Para në dorë)</SelectItem>
                      <SelectItem value="card">Card (Kartë Bankare)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Shuma e Paguar (LEK)</Label>
                  <NumericInput
                    value={editPaid}
                    onChange={(e) => setEditPaid(parseFloat(e.target.value) || 0)}
                    className="font-mono text-lg"
                  />
                </div>
              </div>

              {editPaid < editingSale.total && (
                <div className="bg-rose-100 p-3 rounded-lg text-center">
                  <p className="text-sm text-rose-800">Borxh i ri:</p>
                  <p className="font-mono font-bold text-rose-600">{formatCurrency(editingSale.total - editPaid)}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSaleOpen(false)}>Anulo</Button>
            <Button onClick={handleUpdateSale}>Ruaj Ndryshimet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Manual Debt Modal */}
      <Dialog open={isManualDebtModalOpen} onOpenChange={setIsManualDebtModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Shto Borxh Manual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Klienti *</Label>
              <Select
                value={manualDebtForm.customer_id}
                onValueChange={(v) => setManualDebtForm({ ...manualDebtForm, customer_id: v })}
              >
                <SelectTrigger><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shuma (LEK) *</Label>
              <NumericInput
                value={manualDebtForm.amount}
                onChange={(e) => setManualDebtForm({ ...manualDebtForm, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={manualDebtForm.date}
                onChange={(e) => setManualDebtForm({ ...manualDebtForm, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Shënime</Label>
              <Input
                value={manualDebtForm.notes}
                onChange={(e) => setManualDebtForm({ ...manualDebtForm, notes: e.target.value })}
                placeholder="Përshkrimi i borxhit..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualDebtModalOpen(false)}>Anulo</Button>
            <Button onClick={handleManualDebtSubmit}>Shto Borxh</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Debt Modal */}
      <Dialog open={isEditDebtModalOpen} onOpenChange={setIsEditDebtModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Modifiko Borxhin</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-2">
              <p className="text-sm text-slate-500">{selectedDebt?.type === 'customer' ? 'Klienti' : 'Furnitori'}</p>
              <p className="font-semibold text-lg">{selectedDebt?.entity_name}</p>
            </div>
            <div>
              <Label>Borxhi i Ri (LEK)</Label>
              <NumericInput
                value={editDebtAmount}
                onChange={(e) => setEditDebtAmount(parseFloat(e.target.value) || 0)}
                className="font-mono text-lg"
              />
              <p className="text-xs text-slate-500 mt-1">Ndryshimi manual i borxhit nuk krijon faturë shitjeje.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDebtModalOpen(false)}>Anulo</Button>
            <Button onClick={handleUpdateDebt}>Ruaj Ndryshimet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div >
  );
}
